import { defineAsyncComponent, type AsyncComponentLoader, type Component } from "vue";
import { gettext } from "@/services/translation";
import { showReloadToast } from "@/services/toasts";
import { trackEvent, Event } from "@/services/analytics/sentry";
import { canRetryDynamicImport, isDynamicImportError, recordDynamicImportAttempt } from "@/utilities/dynamicImportRetry";

// Short backoff before each retry attempt (index = attempt number, 0-based).
const RetryDelaysMs = [300, 800, 1500];

/**
 * Drop-in replacement for defineAsyncComponent that retries a failed dynamic import with a short
 * backoff before giving up. Recovers transient network/CDN blips in place, without a full page reload
 * that would discard in-progress user input. See issue #1066.
 */
export function useAsyncComponent<T extends Component>(loader: AsyncComponentLoader<T>) {
  const resilientLoader: AsyncComponentLoader<T> = async () => {
    for (let attempt = 0; ; attempt++) {
      try {
        const component = await loader();
        if (attempt > 0) {
          trackEvent(Event.DynamicImportRecovered, "info");
        }
        return component;
      } catch (error) {
        const canRetry = attempt < RetryDelaysMs.length && isDynamicImportError(error) && canRetryDynamicImport(error, RetryDelaysMs.length);
        if (!canRetry) {
          if (isDynamicImportError(error)) {
            trackEvent(Event.DynamicImportFailed, "warning");
            showReloadToast(gettext.$gettext("Nepodařilo se načíst část stránky. Zkus to prosím znovu."), gettext.$gettext("Obnovit stránku"));
          }
          throw error;
        }
        recordDynamicImportAttempt(error);
        await wait(RetryDelaysMs[attempt]);
      }
    }
  };

  return defineAsyncComponent(resilientLoader);
}

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));
